import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaExchangeAlt, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaCheckCircle } from 'react-icons/fa';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

// NMC's elective guidelines (CBME curriculum) name these as suggested
// non-clinical elective areas alongside ordinary clinical department postings.
const NON_CLINICAL_ELECTIVE_TOPICS = [
  'Research Methodology', 'Research Ethics', 'AI & Computers in Health',
  'Health Management', 'Health Economics', 'Indian System of Medicine',
  'Medical Photography', 'Global Health', 'Evidence Based Medicine',
  'Physiotherapy', 'Nutrition', 'Ethical Use of Technology/AI', 'Literary Activities'
];

const emptyForm = () => ({ department: '', fromDate: '', toDate: '', activitiesPerformed: '', learningOutcomes: '' });

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const UGElectives = ({ onBack, subjects = [] }) => {
  const [electives, setElectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null); // null | 'new' | entry object
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const departmentOptions = [...subjects.map(s => s.name), ...NON_CLINICAL_ELECTIVE_TOPICS];

  const fetchElectives = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/electives/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setElectives(data.electives || []);
    } catch (err) {
      console.error('Error fetching electives:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchElectives(); }, [fetchElectives]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      department: entry.department,
      fromDate: new Date(entry.fromDate).toISOString().slice(0, 10),
      toDate: new Date(entry.toDate).toISOString().slice(0, 10),
      activitiesPerformed: entry.activitiesPerformed || '',
      learningOutcomes: entry.learningOutcomes || ''
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.department || !form.fromDate || !form.toDate) {
      alert('Please fill in Department, From date and To date before saving.');
      return;
    }
    if (new Date(form.toDate) < new Date(form.fromDate)) {
      alert('To date cannot be before From date.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/electives${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchElectives();
      } else {
        alert(data.message || 'Failed to save elective posting');
      }
    } catch (err) {
      console.error('Error saving elective:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this elective posting? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/electives/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchElectives();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting elective:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // entry alone does not flip its review status back to pending.
  const resubmit = async (entry) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/electives/${entry._id}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchElectives();
      else alert(data.message || 'Failed to resubmit');
    } catch (err) {
      console.error('Error resubmitting elective:', err);
    }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaExchangeAlt /> Electives</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Elective postings — mandatory, non-substitutable NMC CBME requirement (1 month between Phase III Part I and Part II, plus two 15-day blocks in Phase III Part II). Log department, duration, activities, and learning outcomes for each.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Add elective posting
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading elective postings...</div>
      ) : electives.length === 0 ? (
        <div className="ug-due-empty">
          No elective postings yet. Log your elective rotations with department, duration, activities, and learning outcomes.
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {electives.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <h4 className="ug-reflection-period" style={{ margin: 0 }}>{entry.department}</h4>
                <span className="ug-reflection-date">{formatDate(entry.fromDate)} – {formatDate(entry.toDate)}</span>
              </div>

              {entry.activitiesPerformed && (
                <>
                  <div className="ug-reflection-section-label">Activities Performed</div>
                  <p className="ug-reflection-text">{entry.activitiesPerformed}</p>
                </>
              )}
              {entry.learningOutcomes && (
                <>
                  <div className="ug-reflection-section-label">Learning Outcomes</div>
                  <p className="ug-reflection-text">{entry.learningOutcomes}</p>
                </>
              )}

              <div className="ug-reflection-card-top" style={{ marginTop: 10 }}>
                {entry.review?.status === 'approved' ? (
                  <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>
                ) : entry.review?.status === 'needs_revision' ? (
                  <span className="ug-status-badge ug-status-revision">Needs revision</span>
                ) : (
                  <span className="ug-status-badge ug-status-pending">Pending sign-off</span>
                )}
              </div>
              {(entry.review?.feedback || entry.supervisorFeedback) && (
                <>
                  <div className="ug-reflection-section-label">Supervisor Feedback</div>
                  <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.review?.feedback || entry.supervisorFeedback}</p>
                </>
              )}
              {entry.review?.reviewerName && (
                <p className="ug-reviewer-tag">
                  {entry.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {entry.review.reviewerName}
                </p>
              )}

              <div className="ug-reflection-card-actions">
                {entry.review?.status !== 'approved' && (
                  <>
                    <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                    <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Delete"><FaTrash /></button>
                  </>
                )}
                {entry.review?.status === 'needs_revision' && (
                  <button className="ug-log-btn" onClick={() => resubmit(entry)}>Resubmit for review</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={electives.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{modalEntry === 'new' ? 'Add elective posting' : 'Edit elective posting'}</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Department</label>
            <select
              className="ug-form-input"
              value={form.department}
              onChange={e => setForm({ ...form, department: e.target.value })}
            >
              <option value="">Select department</option>
              {departmentOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>

            <label className="ug-form-label">From date</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.fromDate}
              onChange={e => setForm({ ...form, fromDate: e.target.value })}
            />

            <label className="ug-form-label">To date</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.toDate}
              onChange={e => setForm({ ...form, toDate: e.target.value })}
            />

            <label className="ug-form-label">Activities performed (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.activitiesPerformed}
              onChange={e => setForm({ ...form, activitiesPerformed: e.target.value })}
            />

            <label className="ug-form-label">Learning outcomes (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.learningOutcomes}
              onChange={e => setForm({ ...form, learningOutcomes: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save elective posting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGElectives;
